/* Hallmark · build · RackFlyoutDrawer · genre: modern-minimal · macrostructure: Slide-out Drawer
 * theme: Cobalt (project-locked) · pre-emit critique: P5 H5 E5 S5 R5 V5
 * states: idle · loading · populated · empty · copying · error
 * touch: targets >= 44px on mobile · keyboard: Escape to dismiss
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ExternalLink,
  Copy,
  Check,
  X,
  Server,
  Zap,
  Weight,
  Thermometer,
  Layers,
  Building2,
  Tag,
  Bookmark,
} from 'lucide-react';
import { api } from '../../utils/api';
import { formatRackName } from '../../utils/rackName.logic';
import { useRackReservations } from '../../hooks/useRackReservations';
import {
  computeMiniElevationSlots,
  computeRackUtilizationMetrics,
  formatShareableRackUrl,
} from '../../utils/rackFlyout.logic';
import {
  normalizeRackStatus,
  normalizeRackRole,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
} from '../../utils/rackLifecycle.logic';
import { getThermalColor } from '../../utils/thermalOverlay.logic';

export interface RackDetail {
  id: number;
  label?: string;
  row_index: number;
  rack_index: number;
  u_slots?: number;
  type?: string;
  is_blocked?: boolean;
  tenant?: string;
  status?: string;
  role?: string;
  max_power_w?: number;
  max_weight_kg?: number;
}

export interface RackFlyoutDrawerProps {
  rack: RackDetail | null;
  dcCode: string;
  onClose: () => void;
  temperature?: number | null;
}

export function RackFlyoutDrawer({ rack, dcCode, onClose, temperature }: RackFlyoutDrawerProps) {
  const [copied, setCopied] = useState(false);

  // Close on Escape key
  useEffect(() => {
    if (!rack) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rack, onClose]);

  // Fetch installed equipment
  const { data: equipment = [], isLoading: isEqLoading } = useQuery<any[]>({
    queryKey: ['rack-flyout-equipment', dcCode, rack?.id],
    queryFn: () => api.get(`/api/datacenters/${dcCode}/racks/${rack!.id}/equipment`).then(res => res.data),
    enabled: !!rack?.id && !!dcCode,
  });

  // Fetch reservations from local storage store
  const totalU = rack?.u_slots || 42;
  const { reservations } = useRackReservations(rack?.id, totalU, equipment);

  // Aggregate metrics
  const metrics = useMemo(() => {
    return computeRackUtilizationMetrics(
      totalU,
      equipment,
      reservations,
      rack?.max_power_w || 5000,
      rack?.max_weight_kg || 1000
    );
  }, [totalU, equipment, reservations, rack]);

  // Compute elevation slots (descending from totalU to 1)
  const elevationSlots = useMemo(() => {
    return computeMiniElevationSlots(totalU, equipment, reservations);
  }, [totalU, equipment, reservations]);

  // Copy canonical direct link
  const handleCopyLink = useCallback(() => {
    if (!rack) return;
    const url = formatShareableRackUrl(window.location.origin, dcCode, rack.id);
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [rack, dcCode]);

  if (!rack) {
    return null;
  }

  const status = normalizeRackStatus(rack.status);
  const role = normalizeRackRole(rack.role, rack.type);
  const statusMeta = RACK_STATUS_METAS[status];
  const roleMeta = RACK_ROLE_METAS[role];
  const title = rack.label || formatRackName(undefined, undefined, rack.row_index, rack.rack_index);

  return (
    <aside
      data-testid="rack-flyout-drawer"
      role="dialog"
      aria-label={`Стойка ${title}`}
      className="absolute top-0 right-0 bottom-0 z-30 w-full sm:w-[380px] lg:w-[420px] bg-bg-panel border-l border-border shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-300 ease-in-out select-none"
    >
      {/* ── Header ── */}
      <div className="p-4 border-b border-border bg-bg-panel/90 flex items-start justify-between gap-3 shrink-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold font-mono uppercase tracking-wider"
              style={{
                backgroundColor: statusMeta.badgeBg,
                borderColor: statusMeta.badgeBorder,
                color: statusMeta.color,
                borderWidth: 1,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusMeta.color }} />
              {statusMeta.labelRu}
            </span>

            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold font-mono"
              style={{
                backgroundColor: roleMeta.badgeBg,
                borderColor: roleMeta.badgeBorder,
                color: roleMeta.color,
                borderWidth: 1,
              }}
            >
              <Tag size={9} />
              {roleMeta.labelRu}
            </span>

            {rack.tenant && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-white/5 border border-white/10 text-text-muted">
                <Building2 size={9} />
                {rack.tenant}
              </span>
            )}
          </div>

          <h2 className="text-base font-bold text-text-primary leading-snug truncate" title={title}>
            {title}
          </h2>

          <p className="text-xs text-text-muted mt-0.5 font-mono">
            Ряд {rack.row_index + 1} · Позиция {rack.rack_index + 1} · {totalU}U
          </p>
        </div>

        {/* 44px close button */}
        <button
          data-testid="rack-flyout-close"
          onClick={onClose}
          aria-label="Закрыть ящик стойки"
          className="w-11 h-11 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md bg-transparent hover:bg-bg-hover text-text-muted hover:text-text-primary active:scale-95 transition-all shrink-0 cursor-pointer"
        >
          <X size={20} />
        </button>
      </div>

      {/* ── Scrollable Body ── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* ── Action buttons (>= 44px touch height) ── */}
        <div className="grid grid-cols-2 gap-2">
          <Link
            to={`/${dcCode}/racks/${rack.id}`}
            data-testid="rack-flyout-passport-link"
            className="flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 rounded bg-accent hover:bg-accent/80 active:scale-98 text-white text-xs font-semibold shadow-xs transition-all text-center"
          >
            <ExternalLink size={14} />
            <span>Паспорт стойки</span>
          </Link>

          <button
            type="button"
            data-testid="rack-flyout-copy-btn"
            onClick={handleCopyLink}
            className="flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 rounded border border-border hover:border-accent/40 bg-bg-app hover:bg-bg-hover active:scale-98 text-text-secondary hover:text-text-primary text-xs font-medium transition-all"
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
            <span>{copied ? 'Скопировано!' : 'Копировать ссылку'}</span>
          </button>
        </div>

        {/* ── Space Capacity Progress Bar ── */}
        <div className="p-3 rounded border border-border bg-bg-app space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-text-muted font-medium">
              <Layers size={13} className="text-accent" />
              Ёмкость шкафа
            </span>
            <span className="font-mono font-bold text-text-primary tabular-nums">
              {metrics.space.utilizationPct}% ({metrics.space.occupiedU + metrics.space.reservedU}/{totalU} U)
            </span>
          </div>

          {/* 3-segment bar: occupied (blue), reserved (amber stripe), available (dark) */}
          <div
            className="h-2 w-full bg-bg-input rounded-full overflow-hidden flex"
            role="progressbar"
            aria-valuenow={metrics.space.utilizationPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Заполнение стойки"
          >
            <div
              style={{ width: `${metrics.space.occupiedPct}%` }}
              className="h-full bg-sky-400 transition-all duration-300"
              title={`Занято оборудованием: ${metrics.space.occupiedU}U (${metrics.space.occupiedPct}%)`}
            />
            <div
              style={{
                width: `${metrics.space.reservedPct}%`,
                backgroundImage: 'repeating-linear-gradient(45deg, #f59e0b, #f59e0b 4px, #d97706 4px, #d97706 8px)',
              }}
              className="h-full transition-all duration-300"
              title={`Зарезервировано: ${metrics.space.reservedU}U (${metrics.space.reservedPct}%)`}
            />
          </div>

          {/* 3 chips */}
          <div className="grid grid-cols-3 gap-1.5 pt-1">
            <div className="px-2 py-1 rounded bg-bg-panel border border-border text-center">
              <span className="block text-[9px] uppercase tracking-wider text-text-muted">Занято</span>
              <span className="text-xs font-bold font-mono text-sky-400 tabular-nums">
                {metrics.space.occupiedU} U
              </span>
            </div>
            <div className="px-2 py-1 rounded bg-bg-panel border border-border text-center">
              <span className="block text-[9px] uppercase tracking-wider text-text-muted">Резерв</span>
              <span className="text-xs font-bold font-mono text-amber-400 tabular-nums">
                {metrics.space.reservedU} U
              </span>
            </div>
            <div className="px-2 py-1 rounded bg-bg-panel border border-border text-center">
              <span className="block text-[9px] uppercase tracking-wider text-text-muted">Свободно</span>
              <span className="text-xs font-bold font-mono text-emerald-400 tabular-nums">
                {metrics.space.availableU} U
              </span>
            </div>
          </div>
        </div>

        {/* ── Power, Weight & Temperature Summary ── */}
        <div className={`grid ${temperature != null ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
          <div className="p-2.5 rounded border border-border bg-bg-app flex items-center gap-2.5">
            <div className="w-8 h-8 rounded bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
              <Zap size={16} />
            </div>
            <div className="min-w-0">
              <span className="block text-[9px] uppercase tracking-wider text-text-muted">Мощность</span>
              <span className="text-xs font-bold font-mono text-text-primary tabular-nums">
                {metrics.totalPowerW} W
              </span>
              <span className="block text-[9px] text-text-muted tabular-nums truncate">
                {metrics.powerLoadPct}% / {metrics.maxPowerW}W
              </span>
            </div>
          </div>

          <div className="p-2.5 rounded border border-border bg-bg-app flex items-center gap-2.5">
            <div className="w-8 h-8 rounded bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
              <Weight size={16} />
            </div>
            <div className="min-w-0">
              <span className="block text-[9px] uppercase tracking-wider text-text-muted">Вес</span>
              <span className="text-xs font-bold font-mono text-text-primary tabular-nums">
                {metrics.totalWeightKg} kg
              </span>
              <span className="block text-[9px] text-text-muted tabular-nums truncate">
                {metrics.weightLoadPct}% / {metrics.maxWeightKg}kg
              </span>
            </div>
          </div>

          {temperature != null && (() => {
            const thermal = getThermalColor(temperature);
            return (
              <div
                data-testid="rack-flyout-temperature"
                className="p-2.5 rounded border border-border bg-bg-app flex items-center gap-2.5"
              >
                <div
                  className="w-8 h-8 rounded flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: thermal?.badgeBg ?? 'rgba(255,255,255,0.05)',
                    border: `1px solid ${thermal?.stroke ?? 'rgba(255,255,255,0.1)'}`,
                    color: thermal?.stroke ?? '#94a3b8',
                  }}
                >
                  <Thermometer size={16} />
                </div>
                <div className="min-w-0">
                  <span className="block text-[9px] uppercase tracking-wider text-text-muted">Темп.</span>
                  <span
                    className="text-xs font-bold font-mono tabular-nums"
                    style={{ color: thermal?.stroke ?? '#e2e8f0' }}
                  >
                    {temperature.toFixed(1)}°C
                  </span>
                  <span className="block text-[9px] text-text-muted tabular-nums truncate" title={thermal?.label}>
                    {thermal?.label.replace(/<|\>|°C|\d+–\d+|\d+/g, '').replace(/[\(\)]/g, '').trim() || 'ASHRAE'}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── Mini-Elevation Schematic ── */}
        <div className="p-3 rounded border border-border bg-bg-app">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted flex items-center gap-1.5">
              <Server size={12} className="text-accent" />
              Мини-элевация ({totalU}U)
            </span>
            <span className="text-[10px] font-mono text-text-muted">Вид спереди</span>
          </div>

          {/* Cabinet Rail Frame */}
          <div
            data-testid="mini-elevation-frame"
            className="border-x-2 border-slate-700 bg-slate-950/60 rounded-xs p-1 max-h-72 overflow-y-auto space-y-0.5"
          >
            {isEqLoading ? (
              <div className="py-8 text-center text-xs text-text-muted">Загрузка оборудования...</div>
            ) : elevationSlots.length === 0 ? (
              <div className="py-8 text-center text-xs text-text-muted">Стойка свободна</div>
            ) : (
              elevationSlots.map(slot => {
                if (slot.type === 'equipment') {
                  return (
                    <div
                      key={`eq-${slot.uStart}`}
                      className="flex items-center gap-1.5 px-1.5 py-1 rounded-xs border border-white/10 text-[10px] font-mono"
                      style={{
                        backgroundColor: `${slot.color}15`,
                        borderLeftColor: slot.color,
                        borderLeftWidth: 3,
                        minHeight: Math.max(24, slot.heightU * 18),
                      }}
                    >
                      <span className="w-6 text-[9px] text-text-muted font-bold shrink-0">
                        {slot.heightU > 1 ? `${slot.uEnd}..${slot.uStart}` : slot.uStart}
                      </span>
                      <div className="min-w-0 flex-1 truncate">
                        <span className="font-semibold text-text-primary truncate block">{slot.label}</span>
                        <span className="text-[9px] text-text-muted">
                          {slot.heightU}U {slot.equipment?.power_w ? `· ${slot.equipment.power_w}W` : ''}
                        </span>
                      </div>
                    </div>
                  );
                }

                if (slot.type === 'reservation') {
                  return (
                    <div
                      key={`res-${slot.uStart}`}
                      className="flex items-center gap-1.5 px-1.5 py-1 rounded-xs border border-amber-500/30 text-[10px] font-mono"
                      style={{
                        backgroundColor: 'rgba(245, 158, 11, 0.08)',
                        borderLeftColor: '#f59e0b',
                        borderLeftWidth: 3,
                        minHeight: Math.max(22, slot.heightU * 18),
                      }}
                    >
                      <span className="w-6 text-[9px] text-amber-400/80 font-bold shrink-0">
                        {slot.heightU > 1 ? `${slot.uEnd}..${slot.uStart}` : slot.uStart}
                      </span>
                      <div className="min-w-0 flex-1 truncate">
                        <span className="font-semibold text-amber-300 truncate flex items-center gap-1">
                          <Bookmark size={9} />
                          {slot.label}
                        </span>
                        <span className="text-[9px] text-amber-400/70">Резерв {slot.heightU}U</span>
                      </div>
                    </div>
                  );
                }

                // Empty unit row
                return (
                  <div
                    key={`empty-${slot.uStart}`}
                    className="flex items-center gap-1.5 px-1.5 py-0.5 text-[9px] font-mono text-text-muted/40 border-b border-white/5"
                  >
                    <span className="w-6 text-text-muted/40 shrink-0">{slot.uStart}</span>
                    <span className="text-[8px] tracking-widest text-text-muted/30">— свободно —</span>
                  </div>
                );
              })
            )}
          </div>

          {/* Zero-U Equipment section if present */}
          {metrics.zeroUEquipment.length > 0 && (
            <div className="mt-2 pt-2 border-t border-border flex items-center gap-1.5 flex-wrap">
              <span className="text-[9px] font-semibold uppercase text-text-muted">Zero-U:</span>
              {metrics.zeroUEquipment.map((eq, i) => (
                <span
                  key={eq.id || i}
                  className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-bg-panel border border-border text-text-secondary"
                >
                  {eq.name || 'Zero-U устройство'}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
